import { useState, useCallback, useEffect, useRef } from "react"
import useT from "utils/hooks/useT"

const ALLOWED_EXTENSIONS = [".pdf", ".docx", ".txt", ".csv", ".png", ".jpg", ".jpeg", ".gif", ".webp"]
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"]
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
const MAX_FILE_COUNT = 3

export default function useFileAttachments(onWarning) {
  const { t } = useT("common")
  const [files, setFiles] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  const dropZoneRef = useRef(null)

  const validate = useCallback((incoming) => {
    const remaining = MAX_FILE_COUNT - files.length
    if (incoming.length > remaining) {
      onWarning?.(t("tooManyFiles"))
      return false
    }
    for (const file of incoming) {
      const ext = file.name.includes(".")
        ? file.name.substring(file.name.lastIndexOf(".")).toLowerCase()
        : ""
      const isAllowedExt = ALLOWED_EXTENSIONS.includes(ext)
      const isAllowedImage = IMAGE_TYPES.includes(file.type)
      if (!isAllowedExt && !isAllowedImage) {
        onWarning?.(t("unsupportedFileType"))
        return false
      }
      if (file.size > MAX_FILE_SIZE) {
        onWarning?.(t("fileTooLarge"))
        return false
      }
    }
    return true
  }, [files.length, onWarning, t])

  const addFiles = useCallback((incoming) => {
    if (validate(incoming)) {
      setFiles(prev => [...prev, ...incoming])
    }
  }, [validate])

  const removeFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }, [])

  const clearFiles = useCallback(() => setFiles([]), [])

  // Drag-drop: listen on window, check if cursor is over our zone
  useEffect(() => {
    const zone = dropZoneRef.current
    if (!zone) return

    const hasFiles = (e) => e.dataTransfer?.types?.includes("Files")

    const handleDragOver = (e) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      const overZone = zone.contains(e.target)
      setIsDragging(overZone)
    }

    const handleDrop = (e) => {
      const overZone = zone.contains(e.target)
      if (overZone && hasFiles(e)) {
        e.preventDefault()
        const dropped = Array.from(e.dataTransfer.files)
        if (dropped.length > 0) addFiles(dropped)
      }
      setIsDragging(false)
    }

    const handleDragEnd = () => setIsDragging(false)
    const handleDragLeave = (e) => {
      if (e.clientX === 0 && e.clientY === 0) {
        setIsDragging(false)
      }
    }

    window.addEventListener("dragover", handleDragOver)
    window.addEventListener("drop", handleDrop)
    window.addEventListener("dragend", handleDragEnd)
    window.addEventListener("dragleave", handleDragLeave)

    return () => {
      window.removeEventListener("dragover", handleDragOver)
      window.removeEventListener("drop", handleDrop)
      window.removeEventListener("dragend", handleDragEnd)
      window.removeEventListener("dragleave", handleDragLeave)
    }
  }, [addFiles])

  // Paste handler — attach to the textarea's onPaste
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items
    if (!items) return
    const pastedFiles = []
    for (const item of items) {
      if (item.kind === "file") {
        const file = item.getAsFile()
        if (file) pastedFiles.push(file)
      }
    }
    if (pastedFiles.length > 0) {
      e.preventDefault()
      addFiles(pastedFiles)
    }
  }, [addFiles])

  return { files, addFiles, removeFile, clearFiles, isDragging, dropZoneRef, handlePaste }
}
